import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert } from '@vtex/shoreline'
import { lighten } from 'polished'

import LineChart from '~/components/Chart/LineChart'
import Loading from '~/components/Loading'
import Panel from '~/components/Panel'
import SelectSearchable from '~/components/SelectSearchable'
import { handleErrorMessage } from '~/helpers/errors'
import { primary as theme } from '~/styles/themes'

import CompareDatePicker from './elements/CompareDatePicker'
import { dropExcludedMetrics, getChartMarkerIndex } from './helpers'
import { defaultOptionsChart, metricsChartConfig } from './options'
import { createTemporalMarkerPlugin } from './plugins/temporalMarkerPlugin'
import incomePreset from './presets/metrics/income'
import totalSpentPreset from './presets/metrics/totalSpent'
import { useChart } from './useChart'

import * as S from './styles'

// import { Container } from './styles';

const Chart: React.FC<ChartProps> = ({
  isValidContent = true,
  chartData,
  chartDataCompare,
  loading = false,
  error = null,
  marker = null,
  initialMetrics,
  additionalMetricsKeys = null,
  selectQuantity = 0,
  isRealtime = false,
  isCompare = false,
  adType,
  excludeMetrics
}) => {
  const { t } = useTranslation()

  const [selectedMetrics, setSelectedMetrics] =
    useState<ChartProps['initialMetrics']>(initialMetrics)

  const { getMetricsOptionsByIndex } = useChart({
    isRealtime,
    adType,
    selectedMetrics,
    selectQuantity,
    excludeMetrics
  })

  // escopo mudou (ex.: perdeu a ancoragem em publisher): métrica excluída sai do gráfico
  useEffect(() => {
    setSelectedMetrics(previous =>
      dropExcludedMetrics(previous, excludeMetrics)
    )
  }, [excludeMetrics])

  const chartDataIsReady = useMemo(() => !!chartData?.date?.length, [chartData])

  const metricsToShow = useMemo(
    () =>
      Object.values(selectedMetrics)
        .map(item => item)
        .filter(item => !!item),
    [selectedMetrics]
  )

  const handleChangeMetrics = ({ data, index }) => {
    setSelectedMetrics(prevState => ({
      ...prevState,
      [index]: data?.value || null
    }))
  }

  /**
   * ## `additionalData` Dados adicionais para o tooltip
   * Aqui vamos manipular os dados adicionais que serão exibidos no tooltip além das métricas
   * exibidas no gráfico. Caso alguma métrica seja selecionada para ser exibida no gráfico, ela
   * será ocultada desta lita.
   */
  const additionalData = useMemo(() => {
    if (!additionalMetricsKeys) return null

    const filterKeys = additionalMetricsKeys.filter(
      item => Object.values(selectedMetrics).indexOf(item) === -1
    )

    if (!filterKeys) return null

    type ConfigKeys = {
      [key in AvailablePresets]?: (index: number) => {
        label: string
        data: string
      }
    }

    const configKeys: ConfigKeys = {
      totalSpent: (index: number) => ({
        label: totalSpentPreset.settings.label,
        data: totalSpentPreset.settings.formatAdditionalData(
          chartData.totalSpentNumber[index]
        )
      }),
      income: (index: number) => ({
        label: incomePreset.settings.label,
        data: incomePreset.settings.formatAdditionalData(
          chartData.incomeNumber[index]
        )
      })
    }

    const getDataByDate = (index: number) =>
      filterKeys.reduce(
        (acc, item) => ({
          ...acc,
          [item]: configKeys?.[item](index)
        }),
        {}
      )

    return chartData?.date.reduce(
      (acc, item, index) => ({
        ...acc,
        [`${item}`]: getDataByDate(index)
      }),
      {}
    )
  }, [
    chartData?.date,
    chartData?.incomeNumber,
    chartData?.totalSpentNumber,
    selectedMetrics,
    additionalMetricsKeys
  ])

  /**
   * Handle data
   */

  const chartConfig = useMemo(
    () =>
      metricsChartConfig({
        data: chartData,
        compareData: chartDataCompare,
        isCompare,
        metricPresets: 'dynamic',
        metrics: metricsToShow
      }),
    [chartData, chartDataCompare, isCompare, metricsToShow]
  )

  /**
   * Config chart
   */

  const optionsChart = useMemo(
    () =>
      defaultOptionsChart({
        metricPresets: 'dynamic',
        metrics: metricsToShow,
        data: chartData,
        additionalData
      }),
    [metricsToShow, chartData, additionalData]
  )

  const markerIndex = getChartMarkerIndex({
    data: chartData,
    compareData: chartDataCompare,
    isCompare,
    marker
  })

  const chartPlugins = useMemo(() => {
    if (markerIndex < 0 || !marker) return []

    return [
      createTemporalMarkerPlugin({
        markerIndex,
        lineColor: marker.lineColor,
        tooltipTitle: marker.tooltipTitle,
        tooltipBody: marker.tooltipBody
      })
    ]
  }, [marker, markerIndex])

  /**
   * Styles select
   */

  const dot = (color = '#ccc') => {
    if (!color || typeof color !== 'string') {
      console.warn('dot: color deve ser uma string válida', { color })
      color = '#ccc'
    }

    return {
      alignItems: 'center',
      display: 'flex',

      ':before': {
        backgroundColor: lighten(0.3, color),
        border: `2px solid ${color}`,
        borderRadius: 4,
        content: '" "',
        display: 'block',
        marginRight: 4,
        height: 12,
        width: 12,
        minWidth: 12
      }
    }
  }

  const customStyles = {
    valueContainer: base => ({
      ...base,
      overflow: 'hidden',
      flexWrap: 'nowrap'
    }),
    singleValue: (styles, { data }) => ({
      ...styles,
      ...dot(data.color),
      whiteSpace: 'normal',
      lineHeight: 0.875,
      fontSize: 13
    }),
    menuList: styles => ({
      ...styles,
      display: 'flex',
      flexDirection: 'column'
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      const { color } = data
      return {
        ...styles,
        position: 'relative',
        backgroundColor: (() => {
          return isSelected || isDisabled
            ? theme.colors.secondaryLighten30
            : theme.colors.white
        })(),
        color: isDisabled ? theme.colors.dark : theme.dark,
        cursor: isDisabled ? 'not-allowed' : 'default',
        fontWeight: isFocused ? 'bold' : 'normal',
        // fontSize: 12,

        ':active': {
          ...styles[':active'],
          backgroundColor: () => {
            if (isFocused) {
              return theme.colors.infoLighten50
            }
            if (!isDisabled) {
              return isSelected
                ? theme.colors.light
                : theme.colors.secondaryLighten30
            }
          }
        },
        ...dot(color)
        // ...(isSelected && metricsToShow.length > 1 ? removeOption() : {})
      }
    }
  }

  return (
    <Panel>
      <div className="wrapperChart">
        <Loading status={loading}>{t('rm:metrics.loading')}...</Loading>

        {!!error && (
          <Alert variant="critical">
            Erro:{' '}
            {handleErrorMessage({
              error,
              fallbackMessage: t('rm:chart.loadingError.fallback')
            })}
          </Alert>
        )}

        <S.ContainerSelects className="selectMetrics" style={{}}>
          {Array.from({ length: selectQuantity }).map((_, index) => (
            <div className="selectMetrics-item" key={index}>
              <SelectSearchable
                options={getMetricsOptionsByIndex[index].options}
                defaultValue={getMetricsOptionsByIndex[index].defaultValue}
                placeholder={t('rm:chart.selectMetric.placeholder')}
                onChange={data => handleChangeMetrics({ data, index: index })}
                isClearable={metricsToShow.length > 1}
                styles={customStyles}
              />
            </div>
          ))}

          <CompareDatePicker />
        </S.ContainerSelects>

        {isValidContent && !loading && !!chartData && (
          <>
            {!!chartDataIsReady && (
              <LineChart
                key={`temporal-marker-${marker?.label || 'none'}-${markerIndex}`}
                datasets={chartConfig.datasets}
                labels={chartConfig.labels}
                optionsChart={optionsChart}
                plugins={chartPlugins}
              />
            )}
          </>
        )}
      </div>
    </Panel>
  )
}

export default Chart
